import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {
    EXAMPLE_TOUR_CONFIG,
    LEAD_BOARD_TOUR_CONFIG,
    SAVED_LIST_TOUR_CONFIG,
    LEAD_REPORT_TOUR_CONFIG,
    MARKETING_AUTOMATIONS_TOUR_CONFIG,
    VIEW_ACTIVITIES_TOUR_CONFIG,
} from './tours';
import type {NutshellTour, NutshellTourConfig, NutshellTourStep, TourStepButton} from './types';

/**
 * A helper to map a tour ID to a tour config. Useful for ensuring that each ID
 * has an associated tour. Also allows for dynamic tour content if we call a
 * helper rather than returning constant.
 */
export function getTourConfig(tourId: NutshellTour): NutshellTourConfig {
    switch (tourId) {
        case 'example-tour':
            return EXAMPLE_TOUR_CONFIG;
        case 'lead-board-tour':
            return LEAD_BOARD_TOUR_CONFIG;
        case 'saved-list-tour':
            return SAVED_LIST_TOUR_CONFIG;
        case 'lead-report-tour':
            return LEAD_REPORT_TOUR_CONFIG;
        case 'marketing-automations-tour':
            return MARKETING_AUTOMATIONS_TOUR_CONFIG;
        case 'view-activities-tour':
            return VIEW_ACTIVITIES_TOUR_CONFIG;
        default:
            exhaustivenessCheck(tourId);
    }
}

/**
 * Returns the button configuration for a tour step based on its properties.
 *
 * - Steps with requiredAction: Only show Close button
 * - Last step (without requiredAction): Show Complete button
 * - Other steps: Show Close (secondary) and Next buttons
 */
export function getStepButtons(
    step: NutshellTourStep,
    isLastStep: boolean,
    onNext: () => void,
    onComplete: (shouldRedirect: boolean) => void,
    tourConfig: NutshellTourConfig
): TourStepButton[] {
    if (step.requiredAction) {
        return [];
    }

    if (isLastStep) {
        const buttons: TourStepButton[] = [
            {
                text: 'Close',
                action: () => onComplete(false),
                secondary: true,
            },
        ];

        // show "Back to 'Explore'" button if not explicitly disabled
        if (tourConfig.redirectToExploreOnComplete !== false) {
            buttons.push({
                text: 'Back to ‘Explore’',
                action: () => onComplete(true),
            });
        }

        return buttons;
    }

    return [
        {
            text: 'Next',
            action: onNext,
        },
    ];
}
